<template>
    <the-header />
    <router-view />
    <the-footer />
</template>

<script>
    import TheHeader from './components/ui/TheHeader.vue'
    import TheFooter from './components/ui/TheFooter.vue'

    export default {
        name: 'App',
        mounted() {
            // Intercept links to use vue router on internal links and add GA events to external and files (downloads and external clicks are handled by GA enhanced events so we haven't added them here. Left in for info)
            window.addEventListener('click', event => {
                // ensure we use the link, in case the click has been received by a subelement
                let { target } = event
                while (target && target.tagName !== 'A') target = target.parentNode
                // handle only links that do not reference external resources
                if (target && target.matches("a:not([href*='://']):not([href*='mailto:']):not([href*='tel:']):not([href*='/_resources/'])") && target.href) {
                    // some sanity checks taken from vue-router:
                    // https://github.com/vuejs/vue-router/blob/dev/src/components/link.js#L106
                    const { altKey, ctrlKey, metaKey, shiftKey, button, defaultPrevented } = event
                    // don't handle with control keys
                    if (metaKey || altKey || ctrlKey || shiftKey) return
                    // don't handle when preventDefault called
                    if (defaultPrevented) return
                    // don't handle right clicks
                    if (button !== undefined && button !== 0) return
                    // don't handle if `target="_blank"`
                    if (target && target.getAttribute) {
                        const linkTarget = target.getAttribute('target')
                        if (/\b_blank\b/i.test(linkTarget)) return
                    }
                    // don't handle same page links/anchors
                    const url = new URL(target.href);
                    if (window.location.pathname !== url.pathname && event.preventDefault) {
                        event.preventDefault();
                        this.$router.push(url.pathname + url.search + url.hash);
                    }
                }
                //    else if (target && target.matches("a[href*='://'], a[href*='mailto:'], a[href*='tel:'], a[href*='/_resources/']")) {
                //        console.log(target);
                //        console.log(this.$route)

                //        // Log a GA Event as:
                //        //
                //        event('filter', { 'event_category': 'work', 'event_label': 'type', 'value': this.filterType });

                //    }
                //    console.log(target && target.matches("a[href*='://'], a[href*='mailto:'], a[href*='tel:'], a[href*='/_resources/']"))
            })
            // Get the main links
            this.$store.dispatch('nav/getNav', "all");
        },
        components: {
            TheHeader,
            TheFooter
        }
    }
</script>

<style>
    /*#region Variables
    --------------------------  */

    :root {
        /*Colours*/
        /*Greens*/
        --green1: #707a62;
        --green2: #46483a;
        --green3: #b2bba6;
        --green4: #dddbc1;
        /*Blacks*/
        --black1: #000;
        --black2: #ccc;
        --black3: #ccc;
        /*Whites*/
        --white1: #fff;
        /*Fonts */
        --font-heading: roboto, sans-serif;
        --font-text: roboto, sans-serif;
        --font-size-0: 0.75rem;
        --font-size-1: 0.875rem;
        --font-size-2: 1rem;
        --font-size-3: 1.125rem;
        --font-size-4: 1.375rem;
        --font-size-5: 1.5rem;
        --font-size-6: 1.75rem;
        --font-size-7: 2.125rem;
        /*Sizing*/
        --header-height: 8.2rem;
    }

    /*#endregion */

    /*#region Classes
    --------------------------  */

        /* Container */
        .container {
            max-width: 75em;
            margin-left: auto;
            margin-right: auto;
        }

        .centre {
            text-align: center;
        }

        /* Flex */
        .flex {
            display: -ms-flexbox;
            display: -webkit-flex;
            display: flex;
            -ms-flex-direction: row;
            -webkit-flex-direction: row;
            flex-direction: row;
            -ms-flex-wrap: wrap;
            -webkit-flex-wrap: wrap;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: stretch;
        }

            .flex.column {
                -ms-flex-direction: column;
                -webkit-flex-direction: column;
                flex-direction: column;
                align-items: stretch;
                justify-content: space-between;
            }

            .flex > * {
                -ms-flex: 0 0 100%;
                -webkit-flex: 0 0 100%;
                flex: 0 0 100%;
            }

        .button {
            text-align: center;
            color: var(--black1);
            display: inline-block;
            -moz-box-sizing: border-box;
            -webkit-box-sizing: border-box;
            box-sizing: border-box;
        }

            .button:hover {
                text-decoration: none;
            }

            .button:after {
                content: "";
                display: block;
                border-bottom: solid 1px transparent;
                margin-top: 0.25em;
                margin-right: auto;
                margin-left: auto;
                width: calc(80% - 0.85em);
                -moz-transition: all linear .2s;
                -o-transition: all linear .2s;
                -webkit-transition: all linear .2s;
                transition: all linear .2s;
            }

            .button:hover:after {
                width: 100%;
                border-bottom-color: var(--black1);
                -moz-transition: all linear .2s;
                -o-transition: all linear .2s;
                -webkit-transition: all linear .2s;
                transition: all linear .2s;
            }

        .fade-enter-active {
            transition: opacity 0.5s ease;
            transition-delay: 0.2s;
        }

        .fade-leave-active {
            transition: opacity 0.5s ease;
            transition-delay: 0.1s;
        }

        .fade-enter-from,
        .fade-leave-to {
            opacity: 0;
        }

    /*#endregion */

    /*#region Typography
    --------------------------  */

        h1, h2, h3, h4, h5 {
            font-family: var(--font-text);
            font-weight: 400;
            line-height: 1.2em;
            margin: 0 0 1rem 0;
        }

        h1 {
            font-size: var(--font-size-4);
            font-weight: 500;
        }

        h2 {
            font-size: var(--font-size-3);
        }

        h3, h4, h5 {
            font-size: var(--font-size-2);
        }

        p {
            font-size: var(--font-size-1);
            line-height: 1.25em;
            margin: 0 0 1em 0;
        }

        li, dt, dd {
            font-size: var(--font-size-2);
            line-height: 1.25em;
            margin: 0 0 1em 0;
        }

            li li {
                font-size: 1em;
            }

        strong {
            font-weight: 700;
        }

        button {
            font-family: var(--font-text);
            font-weight: 300;
            margin: 0;
        }

        body {
            font-family: var(--font-text);
            font-weight: 300;
        }

        img {
            max-width: 100%;
        }

        picture img {
            display: block;
        }

        @media screen and (min-width: 800px) {
            h1 {
                font-size: var(--font-size-5);
            }

            h2 {
                font-size: var(--font-size-4);
            }

            h3, h4, h5 {
                font-size: var(--font-size-3);
            }

            p {
                font-size: var(--font-size-2);
            }

            li, dt, dd {
                font-size: var(--font-size-2);
            }
        }

    /*#endregion */

    /*#region  Main Structure
    -------------------------------------  */

        html {
            font-size: 1em;
            scroll-behavior: smooth;
            min-width: 20em;
        }

        article, aside, details, figcaption, figure, footer, header, hgroup, main, menu, nav, section, summary {
            display: block;
        }

        picture {
            display: inline-block;
        }

        body {
            position: relative;
            text-align: left;
            background-color: var(--white1);
            color: var(--black1);
            width: 100%;
            padding: 0;
            margin: 0;
            min-width: 20em;
            -moz-box-sizing: border-box;
            -webkit-box-sizing: border-box;
            box-sizing: border-box;
        }

            body.menu-open {
                overflow: hidden;
            }

        main {
            position: relative;
            z-index: 2;
            background-color: #fff;
            padding: 1.25em 0;
        }

            main .inner-container {
                padding-top: 1em;
                padding-bottom: 1em;
            }

            main *:last-child {
                margin-bottom: 0;
            }

            main a {
                color: var(--green1);
                text-decoration: none;
                -moz-transition: color .2s linear;
                -o-transition: color .2s linear;
                -webkit-transition: color .2s linear;
                transition: color .2s linear;
            }

                main a:hover {
                    text-decoration: underline;
                }

            main ul {
                padding: 0;
                margin: 1.5em 0;
                list-style-type: none;
            }

                main ul li {
                    padding: 0.5em 1em;
                    margin: 0;
                    border-bottom: solid 1px var(--black3);
                    -moz-box-sizing: border-box;
                    -webkit-box-sizing: border-box;
                    box-sizing: border-box;
                }

                main ul li:last-child {
                    border-bottom: 0;
                }

        @media screen and (min-height: 600px) {
            main .inner-container {
                padding-top: 2em;
                padding-bottom: 2em;
            }
        }

        @media screen and (min-height: 800px) {
            main .inner-container {
                padding-top: 3.25em;
                padding-bottom: 3.25em;
            }
        }

    /*#endregion */

</style>
